import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { certApi, type CertData } from '../../api/client';
import { Icon } from '../../components/Icon';

// ---------- 格式化工具 ----------
function fmtDate(iso?: string | null): string {
  if (!iso) return '';
  const d = iso.slice(0, 10).replace(/-/g, '.');
  return d;
}

function fmtRate(rate?: number): string {
  if (rate === undefined || rate === null) return '—';
  const v = rate * 100;
  const s = (Math.round(v * 10) / 10).toString();
  return `${s}%`;
}

// 证书卡内边距参考 .phone 宽度
const COL = { primary: '#4f46e5', gold: '#b8860b', goldSoft: '#d4af37' };

function MetricBox({ icon, label, value, unit }: { icon: 'clock' | 'book' | 'target' | 'check-circle'; label: string; value: string; unit?: string }) {
  return (
    <div style={{
      background: '#fff', borderRadius: 12, padding: '10px 12px',
      boxShadow: '0 1px 4px rgba(30,41,59,.06)', display: 'flex', alignItems: 'center', gap: 10,
    }}>
      <div style={{
        width: 34, height: 34, borderRadius: 10, flex: 'none',
        background: 'linear-gradient(135deg,#eef2ff,#e0e7ff)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <Icon name={icon} size={18} color={COL.primary} />
      </div>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 11, color: 'var(--gray-500)', whiteSpace: 'nowrap' }}>{label}</div>
        <div style={{ fontSize: 17, fontWeight: 800, color: '#1e293b', lineHeight: 1.25, whiteSpace: 'nowrap' }}>
          {value}{unit ? <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--gray-500)', marginLeft: 2 }}>{unit}</span> : null}
        </div>
      </div>
    </div>
  );
}

export function CertPage() {
  const { code } = useParams<{ code?: string }>();
  const navigate = useNavigate();
  const isVerify = Boolean(code);
  const [data, setData] = useState<CertData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [verifyInput, setVerifyInput] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    const task = code
      ? certApi.verify(code).catch((e: Error) => { throw e; })
      : certApi.my().catch((e: Error) => { throw e; });
    task
      .then(d => { if (alive) setData(d); })
      .catch((e: Error) => { if (alive) { setError(e.message || '加载失败'); setData(null); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  function copyCode() {
    const c = data?.platform_code;
    if (!c) return;
    const done = () => { setCopied(true); setTimeout(() => setCopied(false), 1600); };
    if (navigator.clipboard?.writeText) navigator.clipboard.writeText(c).then(done).catch(() => done());
    else done();
  }

  function submitVerify() {
    const v = (verifyInput || '').trim();
    if (!/^\d{10}$/.test(v)) { setError('请输入 10 位数字人才数字ID'); return; }
    setError('');
    navigate(`/cert/${v}`);
  }

  if (loading) return <div className="loading-state"><span className="spinner" /></div>;

  const meta = data?.metrics;

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(180deg,#eef2ff 0%,#f8fafc 40%)', paddingBottom: 40 }}>
      {/* 标题区 */}
      <div style={{ padding: '22px 20px 6px', textAlign: 'center', position: 'relative' }}>
        <button
          onClick={() => navigate(-1)}
          aria-label="返回"
          style={{
            position: 'absolute', left: 16, top: 22, width: 34, height: 34, borderRadius: '50%',
            border: '1px solid #e2e8f0', background: 'rgba(255,255,255,.9)', color: '#334155',
            display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer',
            boxShadow: '0 1px 4px rgba(30,41,59,.08)',
          }}
        >
          <Icon name="arrow-left" size={18} />
        </button>
        {isVerify ? (
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: COL.gold,
            background: 'rgba(212,175,55,.12)', border: '1px solid rgba(212,175,55,.35)',
            padding: '4px 12px', borderRadius: 999, fontWeight: 600,
          }}>
            <Icon name="shield" size={14} color={COL.gold} />
            档案公开验真
          </div>
        ) : null}
        <h1 style={{ margin: isVerify ? '10px 0 4px' : '4px 0 4px', fontSize: 22, fontWeight: 800, color: '#1e293b', letterSpacing: 1 }}>
          盒智家 · 人才学习档案
        </h1>
        <p style={{ margin: 0, fontSize: 12, color: 'var(--gray-500)', letterSpacing: 2 }}>
          KEEP LEARNING · LIFETIME GROWTH
        </p>
      </div>

      {error ? (
        <div style={{ margin: '24px 20px', padding: '20px 16px', background: '#fff', borderRadius: 14, textAlign: 'center' }}>
          <Icon name="alert-circle" size={30} color="#dc2626" />
          <p style={{ margin: '10px 0 2px', fontSize: 15, fontWeight: 600, color: '#334155' }}>{error}</p>
          {isVerify && <p style={{ margin: 0, fontSize: 12, color: 'var(--gray-500)' }}>请核对人才数字ID后重试</p>}
          <button
            onClick={() => (isVerify ? navigate('/') : navigate('/me'))}
            style={{ marginTop: 16, padding: '9px 22px', border: 'none', borderRadius: 999, background: COL.primary, color: '#fff', fontSize: 14, cursor: 'pointer' }}
          >返回首页</button>
        </div>
      ) : !data?.lifetime || !data?.platform_code ? (
        <div style={{ margin: '24px 20px', padding: '22px 16px', background: '#fff', borderRadius: 14, textAlign: 'center' }}>
          <Icon name="clock" size={30} color="#f59e0b" />
          <p style={{ margin: '10px 0 2px', fontSize: 15, fontWeight: 600, color: '#334155' }}>{data?.message || '尚未生成人才数字ID'}</p>
          <p style={{ margin: 0, fontSize: 12, color: 'var(--gray-500)' }}>完成学员身份绑定后即可自动生成</p>
          <button
            onClick={() => navigate('/me')}
            style={{ marginTop: 16, padding: '9px 22px', border: 'none', borderRadius: 999, background: COL.primary, color: '#fff', fontSize: 14, cursor: 'pointer' }}
          >返回我的</button>
        </div>
      ) : (
        <>
          {/* 证书主体 */}
          <div style={{ padding: '14px 20px 0' }}>
            <div style={{
              position: 'relative', background: 'linear-gradient(180deg,#fffef8 0%,#fffdf2 100%)',
              borderRadius: 18, padding: '22px 18px 18px', overflow: 'hidden',
              border: '2px solid #e6c45f', boxShadow: '0 6px 24px rgba(180,150,50,.16)',
            }}>
              {/* 四角装饰 */}
              {['top:8px;left:8px;border-top:2px solid #d4af37;border-left:2px solid #d4af37;border-top-left-radius:10px',
                'top:8px;right:8px;border-top:2px solid #d4af37;border-right:2px solid #d4af37;border-top-right-radius:10px',
                'bottom:8px;left:8px;border-bottom:2px solid #d4af37;border-left:2px solid #d4af37;border-bottom-left-radius:10px',
                'bottom:8px;right:8px;border-bottom:2px solid #d4af37;border-right:2px solid #d4af37;border-bottom-right-radius:10px',
              ].map(s => (
                <div key={s} style={{ position: 'absolute', width: 18, height: 18, ...Object.fromEntries(s.split(';').filter(Boolean).map(kv => { const i = kv.indexOf(':'); return [kv.slice(0, i).trim(), kv.slice(i + 1).trim()]; })) }} />
              ))}

              {/* 中央编号 */}
              <div style={{ textAlign: 'center', padding: '6px 0 12px' }}>
                <div style={{
                  fontSize: 11, color: COL.gold, letterSpacing: 4, fontWeight: 600, marginBottom: 6,
                }}>LEARNER ID · 人才数字ID</div>
                <div style={{
                  display: 'inline-block', fontSize: 32, fontWeight: 900, color: '#7c5800',
                  letterSpacing: 6, fontVariantNumeric: 'tabular-nums', padding: '2px 14px',
                  borderBottom: '1px dashed #d4af37',
                }}>{data.platform_code}</div>
                <div style={{ marginTop: 8, fontSize: 11, color: 'var(--gray-500)' }}>
                  编号含注册年月日 · 唯一可验真
                </div>
              </div>

              {/* 指标 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 4 }}>
                <MetricBox icon="clock" label="学习累计天数" value={String(meta?.studyDays ?? 0)} unit="天" />
                <MetricBox icon="book" label="完成套题次数" value={String(meta?.studySets || 0)} unit="次" />
                <MetricBox icon="target" label="学习套题覆盖" value={String(meta?.studyDecks || 0)} unit="套" />
                <MetricBox icon="check-circle" label="答题正确率" value={fmtRate(meta?.accuracyRate)} />
              </div>

              {/* 全国排名 */}
              <div style={{
                marginTop: 12, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                background: 'linear-gradient(90deg,#7c3aed,#4f46e5)', borderRadius: 12,
                padding: '12px 8px', color: '#fff',
              }}>
                <Icon name="medal" size={22} color="#ffd166" />
                <div style={{ textAlign: 'center', lineHeight: 1.3 }}>
                  <div style={{ fontSize: 12, opacity: .85 }}>全国综合排名</div>
                  <div style={{ fontSize: 15, fontWeight: 800, marginTop: 2 }}>
                    排序第 {meta?.rank || '—'} 名 <span style={{ opacity: .75, fontWeight: 500 }}>/ 排位前 {(meta?.rankTier ?? 10) * 10}%</span>
                  </div>
                </div>
              </div>

              {/* 任职履历 */}
              {(data.tenures?.length || 0) > 0 ? (
                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: COL.gold, letterSpacing: 2, marginBottom: 6, textAlign: 'center' }}>
                    LEARNING JOURNEY · 学习履历（企业归属）
                  </div>
                  {data.tenures!.map((t, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', gap: 8, padding: '7px 10px',
                      background: 'rgba(212,175,55,.08)', borderRadius: 10, marginBottom: 6, fontSize: 13,
                    }}>
                      <span style={{ flex: 1, color: '#334155', fontWeight: 600 }}>{t.tenant_name}</span>
                      <span style={{
                        fontSize: 10, padding: '2px 8px', borderRadius: 999, fontWeight: 700,
                        color: t.status === 'active' ? '#047857' : '#64748b',
                        background: t.status === 'active' ? '#d1fae5' : '#f1f5f9',
                      }}>{t.status === 'active' ? '在职' : '已离职'}</span>
                      <span style={{ color: 'var(--gray-500)', fontSize: 11, whiteSpace: 'nowrap' }}>
                        {fmtDate(t.started_at)}{t.ended_at ? ` ~ ${fmtDate(t.ended_at)}` : ' 至今'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : null}

              {/* 落款 */}
              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginTop: 12 }}>
                <div style={{ fontSize: 11, color: 'var(--gray-500)', lineHeight: 1.6 }}>
                  <div>注册于 {fmtDate(data.registered_at)}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <Icon name="shield" size={26} color={COL.gold} />
                  <div style={{ fontSize: 9, color: COL.gold, marginTop: 2, letterSpacing: 1 }}>盒智家认证</div>
                </div>
              </div>
            </div>
          </div>

          {/* 操作区 */}
          <div style={{ display: 'flex', gap: 10, padding: '14px 20px 0' }}>
            <button
              onClick={copyCode}
              style={{ flex: 1, padding: '11px 0', borderRadius: 12, background: '#fff', border: '1px solid #c7d2fe', color: COL.primary, fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
            >{copied ? '已复制 ✓' : '复制数字ID'}</button>
            {data.verification_url && !isVerify ? (
              <button
                onClick={() => navigate(`/cert/${data.platform_code}`)}
                style={{ flex: 1, padding: '11px 0', borderRadius: 12, background: COL.primary, border: 'none', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
              >查看验真页</button>
            ) : null}
          </div>

          {/* 验真查询框（公开验真页提供） */}
          {isVerify ? (
            <div style={{ margin: '14px 20px 0', background: '#fff', borderRadius: 14, padding: 14, border: '1px solid #eef2ff' }}>
              <div style={{ fontSize: 12, color: 'var(--gray-500)', marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                <Icon name="search" size={14} color={COL.primary} /> 验真其他学员档案：输入 10 位人才数字ID
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  value={verifyInput}
                  onChange={e => setVerifyInput(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  placeholder="例如 2609040001"
                  style={{ flex: 1, border: '1px solid #e2e8f0', borderRadius: 10, padding: '10px 12px', fontSize: 15, letterSpacing: 2, outline: 'none', fontVariantNumeric: 'tabular-nums' }}
                />
                <button
                  onClick={submitVerify}
                  style={{ padding: '0 18px', borderRadius: 10, background: COL.primary, border: 'none', color: '#fff', fontSize: 14, fontWeight: 700, cursor: 'pointer' }}
                >验真</button>
              </div>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
